import { useNotifications, useTranslation } from '@bluebrick/saas-ui'
import { uniqueId } from 'lodash-es'
import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import { useAppStore } from '@/features/app/useAppStore'
import { emitToParent } from '@/features/iframe/useParentEmit'
import NotificationNewData from '@/features/notifications/components/NotificationNewData.vue'
import { NewDataNotificationId, type LocalNotificationItem } from '@/features/notifications/types'

export const useNotificationsStore = defineStore('notifications', () => {
	const { notifications, notify: addNotification, remove: removeNotification } = useNotifications()
	const appStore = useAppStore()
	const { t } = useTranslation()

	function notify(notification: LocalNotificationItem) {
		if (appStore.isEmbedded && notification.sendToParent) {
			emitToParent('NOTIFY', {
				message: notification.title,
				type: notification.type ?? 'info'
			})
		} else {
			addNotification({
				...notification,
				id: notification.id ?? uniqueId('notification-')
			})
		}
	}

	function notifyInfo(message: string, sendToParent = true) {
		notify({
			title: message,
			type: 'info',
			sendToParent
		})
	}

	function notifySuccess(message: string, sendToParent = true) {
		notify({
			title: message,
			type: 'success',
			sendToParent
		})
	}

	function notifyError(message: string, sendToParent = true) {
		notify({
			title: message,
			type: 'danger',
			sendToParent
		})
	}

	function notifyWarning(message: string, sendToParent = true) {
		notify({
			title: message,
			type: 'warning',
			sendToParent
		})
	}

	function notifyNewData() {
		notify({
			title: t('notification.newDataTitle'),
			component: markRaw(NotificationNewData),
			id: NewDataNotificationId,
			isPersistent: true,
			noClose: true,
			noIcon: true
		})
	}

	function remove(notification: LocalNotificationItem) {
		removeNotification(notification)
	}

	return {
		notifications,
		notify,
		notifyInfo,
		notifyError,
		notifySuccess,
		notifyWarning,
		notifyNewData,
		remove
	}
})
